import { BooleanOperators, FilterOperators, InSetFilterOperator } from '../reportFilters';

// Subset of FilterOperators applicable to COA segment text fields
export type MultiHierarchyFilterOperator =
  | FilterOperators.Equal
  | FilterOperators.NotEqual
  | FilterOperators.Contain
  | FilterOperators.NotContain
  | FilterOperators.Blank
  | FilterOperators.NotBlank;

export type MultiHierarchyFilterField = 'group' | 'code' | 'code_description';
export type MultiHierarchyFilterSource = 'coa' | 'lens';

// Unified leaf filter targeting COA or lens columns.
// Improvement: this should be a discriminated union on field+operator so the compiler can
// narrow `value` (e.g. field:'group' → number, InSet operator → ReadonlyArray<number>,
// field:'code' → string). Currently consumers must use runtime checks to determine value type.
export interface MultiHierarchyFilter {
  field: MultiHierarchyFilterField;
  source: MultiHierarchyFilterSource;
  operator: MultiHierarchyFilterOperator | InSetFilterOperator.InSet | InSetFilterOperator.NotInSet;
  value?: string | number | null | ReadonlyArray<string | number>; // ReadonlyArray when operator is InSet or NotInSet
  segmentId: string | number;
}

// Composite AND/OR combining filters
export interface MultiHierarchyCompositeFilter {
  operator: BooleanOperators;
  value: ReadonlyArray<MultiHierarchyFilter>;
}

export type MultiHierarchyCoaFilter = MultiHierarchyFilter | MultiHierarchyCompositeFilter;

export function isMultiHierarchyCoaCompositeFilter(f: MultiHierarchyCoaFilter): f is MultiHierarchyCompositeFilter {
  return f.operator === BooleanOperators.And || f.operator === BooleanOperators.Or;
}

/**
 * Leaf filter targeting a raw (non-CoA) lens column — e.g. `proposal_name`, `position_group`.
 *
 * Discriminated from `MultiHierarchyFilter` by the PRESENCE of `columnName` and the ABSENCE of the
 * CoA-only keys (`field`, `segmentId`, `source`), mirroring how multi-hierarchy row-group levels
 * discriminate a non-CoA breakdown from a CoA one. Note that `MultiHierarchyFilter.source` is NOT
 * the discriminator: `source` selects a SQL table alias for a rollup-hierarchy column and never
 * refers to a raw lens column — its absence is required only to keep the two shapes exclusive.
 */
export interface MultiHierarchyLensColumnFilter {
  columnName: string;
  operator: MultiHierarchyFilterOperator | InSetFilterOperator.InSet | InSetFilterOperator.NotInSet;
  value?: string | number | null | ReadonlyArray<string | number>; // ReadonlyArray when operator is InSet or NotInSet
}

/**
 * Composite AND/OR over lens-column leaves. Homogeneous by construction: a composite mixing CoA and
 * lens leaves is rejected during parameter extraction, because the two kinds are translated by
 * different SQL strategies (dimension JOINs vs a WHERE on the lens) and their boolean structure
 * cannot be preserved across that split.
 */
export interface MultiHierarchyLensCompositeFilter {
  operator: BooleanOperators;
  value: ReadonlyArray<MultiHierarchyLensColumnFilter>;
}

export type MultiHierarchyLensFilter = MultiHierarchyLensColumnFilter | MultiHierarchyLensCompositeFilter;

/** Any filter entry persisted on a multi-hierarchy report, row group, or column group. */
export type MultiHierarchyReportFilter = MultiHierarchyCoaFilter | MultiHierarchyLensFilter;

function hasCompositeOperator(f: MultiHierarchyReportFilter): boolean {
  return f.operator === BooleanOperators.And || f.operator === BooleanOperators.Or;
}

/**
 * Lens-ness is decided by the presence of `columnName` AND the absence of every CoA-only key.
 *
 * A presence-only check would classify a leaf carrying both `columnName` and `field`/`segmentId` as
 * lens, which drops its CoA dimension JOIN and executes it as a bare `"lens"."columnName" = ?`. It
 * would also hide such a leaf from `isMultiHierarchyMixedSourceComposite`, which decides the same
 * way. Neither is reachable from the UI — this guard exists for hand-authored and migrated configs,
 * where an ambiguous leaf is exactly what turns up.
 */
const COA_ONLY_KEYS = ['field', 'segmentId', 'source'] as const;

function isLensLeafShape(leaf: unknown): leaf is MultiHierarchyLensColumnFilter {
  if (typeof leaf !== 'object' || leaf === null) return false;
  if (!('columnName' in leaf)) return false;
  return COA_ONLY_KEYS.every((key) => !(key in leaf));
}

function compositeChildren(f: MultiHierarchyReportFilter): ReadonlyArray<unknown> {
  const { value } = f as MultiHierarchyCompositeFilter | MultiHierarchyLensCompositeFilter;
  return Array.isArray(value) ? value : [];
}

export function isMultiHierarchyLensColumnFilter(
  f: MultiHierarchyReportFilter
): f is MultiHierarchyLensColumnFilter {
  return !hasCompositeOperator(f) && isLensLeafShape(f);
}

export function isMultiHierarchyLensCompositeFilter(
  f: MultiHierarchyReportFilter
): f is MultiHierarchyLensCompositeFilter {
  if (!hasCompositeOperator(f)) return false;
  const children = compositeChildren(f);
  return children.length > 0 && children.every(isLensLeafShape);
}

/** True for a lens-column leaf or a wholly lens-column composite. */
export function isMultiHierarchyLensFilter(f: MultiHierarchyReportFilter): f is MultiHierarchyLensFilter {
  return isMultiHierarchyLensColumnFilter(f) || isMultiHierarchyLensCompositeFilter(f);
}

/**
 * True for a composite whose children span both CoA and lens leaves. Callers reject these rather
 * than guess a boolean interpretation across the two SQL strategies.
 */
export function isMultiHierarchyMixedSourceComposite(f: MultiHierarchyReportFilter): boolean {
  if (!hasCompositeOperator(f)) return false;
  const children = compositeChildren(f);
  const lensCount = children.filter(isLensLeafShape).length;
  return lensCount > 0 && lensCount < children.length;
}

/** Every lens column referenced by a filter entry (empty for CoA entries). */
export function collectLensFilterColumnNames(f: MultiHierarchyReportFilter): string[] {
  if (isMultiHierarchyLensColumnFilter(f)) return [f.columnName];
  if (isMultiHierarchyLensCompositeFilter(f)) return f.value.map((leaf) => leaf.columnName);
  return [];
}
